import { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../config/firebase";
import { registerWithEmail, loginWithEmail, logout as signOut } from "../utils/auth";
import { UserService, User, Client } from "../services/UserService";
import { FirebaseUserContextService } from "../services/FirebaseUserContextService";
import {
  buildTenantPath,
  extractTenantSlugFromPathname,
  normalizeTenantSlug,
} from "../utils/tenantSlug";


interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  availableClients: Client[];
  selectedClient: Client | null;
  isClientSelected: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, name: string, role: "patient" | "doctor" | "fisioterapeuta" | "interno" | "financeiro", clientId?: string) => Promise<void>;
  logout: () => Promise<void>;
  selectClient: (clientId: string) => Promise<void>;
  clearSelectedClient: () => void;
  requestClientSwitch: () => void;
  getDashboardPath: (role: string, tenantSlug?: string | null) => string; // Adicionado
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const navigate = useNavigate();

  const getCurrentRouteContext = () => {
    if (typeof window === "undefined") {
      return {
        requestedTenantSlug: null,
        isLoginRoute: false,
        isClientSelectionRoute: false,
      };
    }

    const pathname = window.location.pathname;
    const requestedTenantSlug = extractTenantSlugFromPathname(pathname);
    const pathSegments = pathname.split("?")[0].split("/").filter(Boolean);
    const routeSegments = requestedTenantSlug ? pathSegments.slice(1) : pathSegments;

    return {
      requestedTenantSlug,
      isLoginRoute: routeSegments[0] === "login",
      isClientSelectionRoute: routeSegments[0] === "selecionar-cliente",
    };
  };

  const fetchClients = async (currentUser: User) => {
    try {
      const clients = await UserService.getAvailableClients(currentUser.userId);
      setAvailableClients(clients);

      let clientToSelect: Client | null = null;
      const { requestedTenantSlug, isLoginRoute, isClientSelectionRoute } = getCurrentRouteContext();
      const matchingClientFromSlug = requestedTenantSlug
        ? clients.find(
            (client) =>
              client.clientSlug &&
              normalizeTenantSlug(client.clientSlug) === requestedTenantSlug
          ) || null
        : null;

      if (matchingClientFromSlug) {
        console.log(`Found tenant from route, selecting client ${matchingClientFromSlug.clientId}.`);
        clientToSelect = matchingClientFromSlug;
      } else if (clients.length === 1) {
        console.log("Found one client, auto-selecting.");
        clientToSelect = clients[0];
      } else if (!isLoginRoute && !isClientSelectionRoute && currentUser.current_client_id) {
        clientToSelect = clients.find(c => c.clientId === currentUser.current_client_id) || null;
      }

      if (clientToSelect) {
        setSelectedClient(clientToSelect);
        if (currentUser.current_client_id !== clientToSelect.clientId) {
          console.log(`Persisting client selection: ${clientToSelect.clientId}`);
          const updatedUser = await UserService.setCurrentClient(currentUser.userId, clientToSelect.clientId);
          if (updatedUser) {
            setUser(updatedUser);
          }
        }
      } else {
        setSelectedClient(null);
      }
    } catch (error) {
      console.error("[AuthContext] Error in fetchClients:", error);
      setAvailableClients([]);
      setSelectedClient(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      //console.log("AuthContext.tsx - Entrou na área de login");
      setIsLoading(true);
      try {
        if (firebaseUser) {
          // Garante token atualizado (claims recentes) antes das chamadas ao Supabase.
          await firebaseUser.getIdToken(true).catch(() => null);
          const userData = await UserService.findOrRelinkUserFromFirebaseAuth(
            firebaseUser.uid,
            firebaseUser.email
          );
          //console.log("AuthContext.tsx - Usuário identificado");
          if (userData) {
            setUser(userData);
            await fetchClients(userData);
          } else {
            setUser(null);
          }
        } else {
          // No user is logged in.
          setUser(null);
        } 
      } catch (error) {
        console.error("[AuthContext] Error during auth state change:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    FirebaseUserContextService.syncAuthContext(user, selectedClient).catch((error) => {
      console.warn("[AuthContext] Falha ao sincronizar contexto do usuário no Firestore:", error);
    });
  }, [
    selectedClient?.clientId,
    selectedClient?.userRole,
    user?.firebase_uid,
    user?.userDisplayName,
    user?.userEmail,
    user?.userGlobalRole,
    user?.userId,
    user?.userRole,
    user?.current_client_id,
  ]);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const firebaseUser = await loginWithEmail(email, password);
      // Força refresh após login para garantir claims atualizadas no JWT.
      await firebaseUser.getIdToken(true).catch(() => null);
      const userData = await UserService.findOrRelinkUserFromFirebaseAuth(
        firebaseUser.uid,
        firebaseUser.email || email
      );
      if (!userData) {
        throw new Error("Usuário não encontrado no Supabase");
      }
      setUser(userData);
      await fetchClients(userData);
      return userData;
    } catch (error) {
      console.error("[AuthContext] Login failed:", error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    role: "patient" | "doctor" | "fisioterapeuta" | "interno" | "financeiro",
    clientId?: string
  ) => {
    try {
      // 1. Passar o clientId para registerWithEmail
      // 2. Remover a chamada duplicada ao UserService.createUser
      const firebaseUser = await registerWithEmail(email, password, name, role, clientId);
      
      // Apenas buscar os dados do usuário recém-criado, que já foi inserido no DB por registerWithEmail
      const userData = await UserService.findOrRelinkUserFromFirebaseAuth(
        firebaseUser.uid,
        firebaseUser.email || email
      );

      if (userData) {
        setUser(userData);
        await fetchClients(userData);
      } else {
        throw new Error("Falha ao buscar dados do usuário recém-criado.");
      }
    } catch (error) {
      console.error("[AuthContext] Registration failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    if (user) {
      await UserService.updateUser(user.userId, { current_client_id: null });
    }
    await signOut();
    setUser(null);
    setSelectedClient(null);
    setAvailableClients([]);
  };

const getDashboardPath = (role: string, tenantSlug?: string | null): string => {
  const roleDashboardPaths: { [key: string]: string } = {
    superAdmin: "/super-admin",
    admin: "/admin",
    doctor: "/doctor/dashboard",
    patient: "/patient/dashboard",
    fisioterapeuta: "/fisioterapeuta/dashboard",
    interno: "/admin",
    financeiro: "/admin/financial-professionals",
  };

  const currentTenantSlug =
    tenantSlug ??
    (typeof window !== "undefined"
      ? extractTenantSlugFromPathname(window.location.pathname)
      : null);

  return buildTenantPath(roleDashboardPaths[role] || "/", currentTenantSlug);
};

// ...

  const selectClient = async (clientId: string) => {
    if (!user) throw new Error("User not authenticated");
    try {
        const updatedUser = await UserService.setCurrentClient(user.userId, clientId);
        const client = availableClients.find(c => c.clientId === clientId);

        if (updatedUser && client) {
            setUser(updatedUser);
            setSelectedClient(client);
            const redirectPath = getDashboardPath(client.userRole, client.clientSlug);
            navigate(redirectPath, { replace: true });
        } else {
            console.error("[AuthContext] Failed to select client: updatedUser or client not found.", { updatedUser, client });
            throw new Error("Failed to select client or client not found.");
        }
    } catch (error) {
        console.error("Error selecting client:", error);
        throw error;
    }
  };
  
  const clearSelectedClient = () => {
      setSelectedClient(null);
      if(user) {
        UserService.updateUser(user.userId, { current_client_id: null });
      }
  };
  
  const requestClientSwitch = async () => {
    if (user) {
      const updatedUser = await UserService.updateUser(user.userId, { current_client_id: null });
      if (updatedUser) {
        setUser(updatedUser);
      } else {
        setUser((currentUser) =>
          currentUser
            ? {
                ...currentUser,
                current_client_id: null,
              }
            : currentUser
        );
      }
    }
    setSelectedClient(null);
    navigate("/selecionar-cliente", { replace: true });
  };


  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      availableClients,
      selectedClient,
      isClientSelected: !!selectedClient,
      login,
      register,
      logout,
      selectClient,
      clearSelectedClient,
      requestClientSwitch,
      getDashboardPath // Adicionado
    }}>
      {children}
    </AuthContext.Provider>
  );
};
